import {
    type AhaResult,
    type CmmArtifactFinalPageExperiment_ExperimentParam_Value,
    type Guide,
    type OnboardingChatResults,
    type OnboardingChatResultsTooltipData,
    type OnboardingChatResultsTooltipType,
} from '@fruitful/types';
import {
    createContext,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    useContext,
} from 'react';

import type { ConversationalMMTrackEvent } from '../analytics/events';
import {
    type OnboardingResultsSlideConfig,
    type TerminalSlideAction,
} from '../types';

/**
 * Deliberately separate from ConversationalMMContext.tsx (the Provider),
 * which needs the slide registry (components/constants.ts) to resolve
 * `getResultsSlides`. That registry imports every slide component, and each
 * slide component needs `useConversationalMMContext` — if this hook lived in
 * the same file as the Provider, that would be a require cycle:
 * Provider -> constants.ts -> a slide -> Provider. Slide components (and
 * anything else that only needs to *read* the context) should import from
 * here, not from ConversationalMMContext.tsx.
 */
export interface ConversationalMMContextValue {
    results: OnboardingChatResults;
    setResults: Dispatch<SetStateAction<OnboardingChatResults>>;
    ahaResult: AhaResult;
    slideIndex: number;
    setSlideIndex: (index: number) => void;
    slides: readonly OnboardingResultsSlideConfig[];
    slideCount: number;
    lastIndex: number;
    tooltipSequence: OnboardingChatResultsTooltipType[];
    setTooltipSequence: Dispatch<
        SetStateAction<OnboardingChatResultsTooltipType[]>
    >;
    activeTooltip: OnboardingChatResultsTooltipType | null;
    setActiveTooltip: Dispatch<
        SetStateAction<OnboardingChatResultsTooltipType | null>
    >;
    tooltipData: OnboardingChatResultsTooltipData | null;
    setTooltipData: Dispatch<
        SetStateAction<OnboardingChatResultsTooltipData | null>
    >;
    showGuidedTooltips: boolean;
    hasSeenGuidedTooltips: boolean;
    onGuidedTooltipsSeen: () => void;
    onTrackEvent: ConversationalMMTrackEvent;
    terminalAction: TerminalSlideAction;
    guides: Guide[];
    cmmFinalArtifactSegment?: CmmArtifactFinalPageExperiment_ExperimentParam_Value;
    renderAccountForm: () => ReactNode;
}

export const ConversationalMMContext =
    createContext<ConversationalMMContextValue | null>(null);

export function useConversationalMMContext(): ConversationalMMContextValue {
    const context = useContext(ConversationalMMContext);
    if (context == null) {
        throw new Error(
            'useConversationalMMContext must be used within a ConversationalMMProvider',
        );
    }
    return context;
}
